import clsx from "clsx";
import { useState } from "react";
import toast from "react-hot-toast";

import { useRoadie } from "@web-app/api";
import {
  AccordionV2,
  Avatar,
  Button,
  CopyableField,
  DateTimeTz,
  LoadingIcon,
  RefreshButton,
} from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import { RoadieShipment } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

const RoadieShipmentDetails = ({
  shipment,
  timezone,
}: {
  shipment: RoadieShipment;
  timezone: keyof typeof timeZoneMap;
}) => {
  const currentUser = useDispatchStore((state) => state.user);
  const getTrackingLink = () => {
    const isTesting =
      import.meta.env.DEV ||
      currentUser?.currentWorkspaceId === "6765d9b90a9de18927e93b71";
    if (isTesting) {
      return `https://track-sandbox.roadie.com/id/${shipment.tracking_number}`;
    }
    return `https://track.roadie.com/id/${shipment.tracking_number}`;
  };
  return (
    <div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Cost</span>
          <span className="tw-details-field-value !text-xs font-semibold">
            {`$${shipment.price}`}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Estimated Distance</span>
          <span className="tw-details-field-value flex items-center gap-2">
            {`${shipment.estimated_distance?.toFixed(2)} miles`}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Scheduled Start Time</span>
          <span className="tw-details-field-value">
            <DateTimeTz
              start={new Date(shipment.deliver_between.start)}
              timezone={timezone}
              presentation="default"
            />
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Scheduled End Time</span>
          <span className="tw-details-field-value">
            <DateTimeTz
              start={new Date(shipment.deliver_between.end)}
              timezone={timezone}
              presentation="default"
            />
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title"> Length</span>
          <span className="tw-details-field-value">
            {shipment.items.map((item) => item.length).join(", ")}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title"> Width</span>
          <span className="tw-details-field-value">
            {shipment.items.map((item) => item.width).join(", ")}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title"> Height</span>
          <span className="tw-details-field-value">
            {shipment.items.map((item) => item.height).join(", ")}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title"> Weight</span>
          <span className="tw-details-field-value">
            {shipment.items.map((item) => item.weight).join(", ")}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title flex items-center">
            Assignee
          </span>
          <span className="flex items-center gap-2">
            {!shipment.driver?.name && (
              <span className="tw-details-field-value">No Assignee</span>
            )}
            {shipment.driver?.name && (
              <>
                <span>
                  <Avatar image={undefined} size={10} />
                </span>
                <span className="flex flex-col">
                  <span className="tw-details-field-value !font-semibold break-words">
                    {shipment.driver?.name}
                  </span>
                  <CopyableField
                    icon="fasPhone"
                    value={shipment.driver?.phone}
                    appearance="inline"
                    className="text-xs font-normal"
                  />
                </span>
              </>
            )}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title flex items-center">
            Tracking Link
          </span>
          <span
            className="tw-details-field-value"
            style={{ overflowWrap: "anywhere" }}
          >
            <CopyableField
              value={getTrackingLink()}
              appearance="link-inline"
              className="text-xs font-normal"
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default function RoadieDeliveryInfo({
  entity,
  entityId,
  timezone,
}: {
  entity: string;
  entityId: string;
  timezone: keyof typeof timeZoneMap;
}) {
  const {
    data: shipments,
    isLoading,
    refetch,
    isRefetching,
  } = useRoadie(entity, entityId);

  const [isCancelling, setIsCancelling] = useState(false);

  if (isLoading || isRefetching)
    return <LoadingIcon className="w-8 h-8 m-auto mt-10" />;

  if (!shipments || shipments.length === 0)
    return <div>Could not fetch Roadie shipments</div>;

  const handleCancel = async () => {
    setIsCancelling(true);
    const resp = await http.delete(
      `/integrations/roadie/${entity}/${entityId}/shipments`,
    );
    if (resp.status === 200) {
      toast.success("Delivery canceled successfully");
      refetch();
      setIsCancelling(false);
    }
  };

  const canCancel = () => {
    const noDriverAssigned = shipments.every(
      (shipment) => shipment.driver === undefined,
    );
    return noDriverAssigned;
  };

  if (shipments.length === 1) {
    return (
      <div className="flex flex-col overflow-y-auto max-h-screen">
        <div className="flex justify-end mr-5 gap-2">
          <RefreshButton onClick={() => refetch()} />
          <Button
            size="sm"
            onClick={handleCancel}
            color="red"
            disabled={!canCancel()}
            isLoading={isCancelling}
          >
            {isCancelling ? "Cancelling..." : "Cancel Shipment"}
          </Button>
        </div>
        <RoadieShipmentDetails shipment={shipments[0]} timezone={timezone} />
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-y-auto max-h-screen">
      <div className="flex justify-end mr-5 gap-2">
        <RefreshButton onClick={() => refetch()} />
        <Button
          size="sm"
          onClick={handleCancel}
          color="red"
          disabled={!canCancel()}
          isLoading={isCancelling}
        >
          {isCancelling ? "Cancelling..." : "Cancel Shipment"}
        </Button>
      </div>
      {shipments.map((shipment, index) => (
        <AccordionV2 key={index} defaultOpen={index == 0} isBorderless>
          <AccordionV2.Label textAlign="left" labelClassName="pl-4">
            <span className={clsx("text-sm font-medium")}>
              {shipment.alternate_id_1}
            </span>
          </AccordionV2.Label>
          <AccordionV2.Content>
            <RoadieShipmentDetails shipment={shipment} timezone={timezone} />
          </AccordionV2.Content>
        </AccordionV2>
      ))}
    </div>
  );
}
